<template>
  <div ref="container" v-resize="onResize" class="bike-canvas" :class="{ 'is-interactive': interactive, 'is-pointing': !!hover }">
    <div v-if="interactive && buffer.length" class="bike-stage-mark" :style="markStyle" aria-hidden="true"></div>
    <canvas ref="canvas"></canvas>
    <template v-if="interactive && buffer.length">
      <svg v-if="hover && hover.ring" :key="'ring-' + hover.key" class="bike-hotspot-ring" :style="hover.ring" viewBox="0 0 100 100" aria-hidden="true">
        <circle class="bike-hotspot-pulse" cx="50" cy="50" r="48"/>
        <circle class="bike-hotspot-dash" cx="50" cy="50" r="48" pathLength="100"/>
      </svg>
      <div v-if="hover" class="bike-hotspot-tip" :class="'is-' + hover.side" :style="hover.tip" aria-hidden="true" @mouseenter="keepHover" @mouseleave="leaveHover" @click="chooseHover">
        <strong>{{ hover.label }}</strong>
        <span class="bike-hotspot-price">{{ hover.price }}</span>
        <span class="bike-hotspot-name">{{ hover.name }}</span>
        <span class="bike-hotspot-cta">Change &rarr;</span>
      </div>
      <div class="bike-stage-tools">
        <div v-if="views.length > 1" class="bike-views" role="group" aria-label="Preview view">
          <button
            v-for="v in views"
            :key="v.id"
            type="button"
            :class="{ 'is-active': view === v.id }"
            :aria-pressed="view === v.id ? 'true' : 'false'"
            @click="setView(v.id)"
          >{{ v.label }}</button>
        </div>
        <button v-if="canRide" type="button" class="bike-ride" :class="{ 'is-riding': riding }" :disabled="riding" @click="ride()">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2.6"/><path d="M12 9.4V3.2M14.25 13.3l5.35 3.1M9.75 13.3l-5.35 3.1"/></svg>
          <span>Ride</span>
        </button>
      </div>
      <transition name="bike-speedo">
        <div v-if="speedo" class="bike-speedo" role="status" aria-live="off">
          <svg viewBox="0 0 120 70" aria-hidden="true">
            <path class="bike-speedo-track" d="M12 62a48 48 0 0 1 96 0" pathLength="100"/>
            <path class="bike-speedo-value" d="M12 62a48 48 0 0 1 96 0" pathLength="100" :stroke-dasharray="speedo.arc + ' 100'"/>
            <line class="bike-speedo-needle" x1="60" y1="62" x2="60" y2="22" :transform="'rotate(' + speedo.needle + ' 60 62)'"/>
          </svg>
          <div><strong>{{ speedo.kmh }}</strong><span>km/h</span><small>{{ speedo.rpm }} rpm</small></div>
        </div>
      </transition>
    </template>
    <div v-if="failedGroupset" class="bike-preview-error" role="status">
      <span>Some groupset images are unavailable.</span>
      <button type="button" @click="mountBike">Retry images</button>
    </div>
  </div>
</template>

<script>

  import { CONSTANTS, loadImage, containImage } from '@/utils';
  import { framesetArtwork } from '@/utils/framesetArtwork';
  import { drivetrainProfile, drivetrainLayers, drivetrainChain, loopPins, drawChainLoop, chainSeams, arcPoints, drawChainLinks, catalogueCalibration, calibrateCatalogueLayer, catalogueChain, pedalMount } from '@/utils/drivetrain';

  import { cockpitPoint, handlebarLayer, handlebarLeverPoint, leverLayer } from '@/utils/cockpit';
  import { leverLandmarks, silhouette, seatOnBar, fitLever, calibratedHoodLength } from '@/utils/hoodFit';

  import { wheelArtworkOrigin, wheelArtworkRadius, wheelLevel, unlevelPoint, wheelViewportFit, unfitPoint } from '@/utils/wheels';

  import { appBase } from '@/utils/basePath';
  import { euros } from '@/utils/partSpecs';
  import { PART_LABELS, partStep, ease, clamp, wheelPart, crankPart, layerMatrix, layerBox, boundsOf, opaqueAt, frameRect, lerpRect } from '@/utils/bikeMotion';

  const VIEWS = [
    { id: 'full', label: 'Full bike' },
    { id: 'cockpit', label: 'Cockpit', parts: /^(bars|groupsetsBar)$/, pad: .3 },
    { id: 'drivetrain', label: 'Drivetrain', parts: /^(groupsetsMiddle|groupsetsLeft|groupsetsGear)$/, pad: .2 }
  ];

  // Rear wheel turns per crank turn (~52x18) and 700c rolling radius in metres.
  const GEAR_RATIO = 2.9;
  const WHEEL_RADIUS_M = .335;

  const GRUOPSET_ANCHORS = {
    groupsetsLeft: 'imageBack',
    groupsetsMiddle: 'imageFront',
    groupsetsBar: 'imageBar',
    groupsetsBrakeLeft: 'imageBrake',
    groupsetsBrakeRight: 'imageBrake',
    groupsetsCapilierLeft: 'imageBrakeCapilierRear',
    groupsetsCapilierRight: 'imageBrakeCapilierFront',
    groupsetsGear: 'imageGearShift'
  };

  // Cage geometry, as stored on a FramesetCage row (camelCased by the API).
  const CAGE_FIELDS = [
    'cageSeatX', 'cageSeatY', 'cageSeatAngle', 'cageSeatFlip',
    'cageDownX', 'cageDownY', 'cageDownAngle', 'cageDownFlip',
    'cageScale'
  ];

  const GROUPSET_ANGLE = {
    groupsetsCapilierLeft: 'groupsetCapilierRearAngle',
    groupsetsCapilierRight: 'groupsetCapilierFrontAngle',
    groupsetsGear: 'groupsetCapilierMiddleAngle'
  };

  function radians( degree ) {
    return degree * Math.PI / 180;
  }

  export default {

    props: {
        // Logical canvas size. The bike is drawn at fixed pixel sizes around the
        // canvas centre, so a tighter canvas = bigger bike on screen (contain-fit
        // keeps the aspect, desktop and mobile alike).
        width: { type: Number, default: 1440 },
        height: { type: Number, default: 860 },
        items: { type: Array, default: () => [] },
        editable: { type: Boolean, default: false }, // New prop
        moveFrameMode: { type: Boolean, default: false },
        // Super-user mode: drag/rotate the two bottle cages individually
        cageEditMode: { type: Boolean, default: false },
        // FramesetCage rows: cage geometry per (frameset, cage[, colour])
        cageGeometry: { type: Array, default: () => [] },
        // Customer preview: clickable parts, camera views, ride and change animations.
        interactive: { type: Boolean, default: false }
    },
    data() {
      return {
        context: null,
        buffer: [],
        renderVersion: 0,
        failedGroupset: false,
        // Interaction State
        dragging: null,
        dragStart: { x: 0, y: 0 },
        initialAnchor: { x: 0, y: 0 },
        hover: null,
        view: 'full',
        riding: false,
        speedo: null
      }
    },
    created() {
      // Per-frame animation state stays out of Vue's reactivity on purpose.
      this.motion = {};
      this.tracks = [];
      this.camRect = null;
      this.frameId = null;
    },
    mounted() {
      this.onResize();
      this.getContext();
      this.mountBike();
      
      // Delated resize to handle dialog transitions
      setTimeout(() => this.onResize(), 100);
      setTimeout(() => this.onResize(), 300);
      
      // Add Listeners
      const canvas = this.$refs.canvas;
      canvas.addEventListener('mousedown', this.handleMouseDown);
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.handleMouseUp);
      canvas.addEventListener('wheel', this.handleWheel, { passive: false });
      canvas.addEventListener('dblclick', this.handleDblClick);
      canvas.addEventListener('mousemove', this.handlePointer);
      canvas.addEventListener('mouseleave', this.leaveHover);
      canvas.addEventListener('click', this.handleClick);
    },
    beforeDestroy() {
        this.renderVersion++;
        cancelAnimationFrame(this.frameId);
        cancelAnimationFrame(this.pointerFrame);
        const canvas = this.$refs.canvas;
        if(canvas) canvas.removeEventListener('mousemove', this.handlePointer);
        if(canvas) canvas.removeEventListener('mouseleave', this.leaveHover);
        if(canvas) canvas.removeEventListener('click', this.handleClick);
        if(canvas) canvas.removeEventListener('mousedown', this.handleMouseDown);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        if(canvas) canvas.removeEventListener('wheel', this.handleWheel);
        if(canvas) canvas.removeEventListener('dblclick', this.handleDblClick);
    },
    watch: {
      width: 'resizeCanvas',
      height: 'resizeCanvas',
      cageEditMode( ) {
        this.liveCageScale = null;
        this.draw();
      },
      composition: 'mountBike',
      buffer: {
          handler: 'onBuffer',
          deep: true
      }
    },
    computed: {
      markStyle() {
        const url = `url(${appBase}/velodrom-logo.png)`;
        return { WebkitMaskImage: url, maskImage: url };
      },
      views() {
        const types = this.buffer.map(item => item.type);
        return VIEWS.filter(v => !v.parts || types.some(type => v.parts.test(type)));
      },
      canRide() {
        return this.buffer.some(item => /^wheels(Left|Right)$/.test(item.type));
      },
      composition() {

        var frameset = this.items[0];
        if ( ! frameset || ! frameset.item ) return null;

        var framesetProps = this.itemProps( frameset ), chain = null, itemProps;
        // Cage geometry belongs to the (frameset, cage) pair, not the frameset
        // alone: cage models differ in size and plate position, so one shared
        // set of anchors cannot fit them all.
        const cageProps = this.cageProps( frameset, framesetProps );
        const itemAnchors = {};

        const anchors = {
          bars: cockpitPoint(framesetProps, 'bar'),
          wheelsLeft: {
            x: framesetProps.leftWheelX || 0,
            y: framesetProps.leftWheelY || 0
          },
          wheelsRight: {
            x: framesetProps.rightWheelX || 0,
            y: framesetProps.rightWheelY || 0
          },
          tyresLeft: {
            x: framesetProps.leftWheelX || 0,
            y: framesetProps.leftWheelY || 0
          },
          tyresRight: {
            x: framesetProps.rightWheelX || 0,
            y: framesetProps.rightWheelY || 0,
          },
          seatposts: {
            x: framesetProps.seatpostX || 0,
            y: framesetProps.seatpostY || 0
          },
          saddles: {
            x: framesetProps.saddleX || 0,
            y: framesetProps.saddleY || 0,
          },
          cagesSeat: {
            x: cageProps.cageSeatX || 0,
            y: cageProps.cageSeatY || 0
          },
          cagesDown: {
            x: cageProps.cageDownX || 0,
            y: cageProps.cageDownY || 0
          },
          groupsetsLeft: {
            x: framesetProps.leftWheelX || 0,
            y: framesetProps.leftWheelY || 0
          },
          groupsetsMiddle: {
            x: framesetProps.groupsetMiddleX || 0,
            y: framesetProps.groupsetMiddleY || 0
          },
          groupsetsBar: cockpitPoint(framesetProps, 'groupsetBar'),
          groupsetsBrakeLeft: {
            x: framesetProps.leftWheelX || 0,
            y: framesetProps.leftWheelY || 0
          },
          groupsetsBrakeRight: {
            x: framesetProps.rightWheelX || 0,
            y: framesetProps.rightWheelY || 0
          },
          groupsetsCapilierLeft: {
            x: framesetProps.groupsetCapilierRearX || 0,
            y: framesetProps.groupsetCapilierRearY || 0
          },
          groupsetsCapilierRight: {
            x: framesetProps.groupsetCapilierFrontX || 0,
            y: framesetProps.groupsetCapilierFrontY || 0
          },
          groupsetsGear: {
            x: framesetProps.groupsetCapilierMiddleX || 0,
            y: framesetProps.groupsetCapilierMiddleY || 0
          }
        };
        
        console.log('Bike: FramesetProps', framesetProps);
        console.log('Bike: Anchors', anchors);

        const items = this.items.map(( a, order, color ) => {

          if ( ! a.item || a.item.noCages || a.item.noPedals || ! ( color = a.item.colors[ a.color ])) return;

          const props = {
            anchor: a.item.type,
            type: a.item.type,
            image: color.color.customImage || color.color.image,
            scale: CONSTANTS[ a.item.type ].scale || 1,
            index: CONSTANTS[ a.item.type ].zIndex,
            origin: { x: a.item.originX, y: a.item.originY },
            order
          };

          switch ( a.item.type ) {
            case 'framesets':

              Object.assign(props, framesetArtwork(props));
              props.scale = CONSTANTS['framesets'].scale || 1.2;
              props.imageScale = framesetProps.framesetScale || props.scale;
              frameset = props;

              break;

            case 'bars':

              itemProps = this.itemProps( a );
              Object.assign(props, handlebarLayer(props, itemProps, framesetProps));
              itemAnchors.groupsetsBar = { ...handlebarLeverPoint(props.image, itemProps), type: 'bars' };

              break;

            case 'groupsets': {

              itemProps = this.itemProps( a );
              chain = {
                front: itemProps.chainFrontDiameter || 0,
                rear: itemProps.chainRearDiameter || 0,
                break: {
                  x: itemProps.brakeFrontX,
                  y: itemProps.brakeFrontY
                }
              };

              const profile = drivetrainProfile(a.item.name);
              // Lever dimensions belong to the groupset, not the resolution or
              // padding of the selected handlebar image.
              const matched = profile ? drivetrainLayers(profile, props, framesetProps.wheelScale || 1) : [];
              const calibration = catalogueCalibration(a.item.name);
              const pedal = pedalMount(profile, calibration);
              if (pedal) itemAnchors.pedals = pedal;
              var rot;
              const legacy = Object.keys( GRUOPSET_ANCHORS ).map( key => {
                if (key === 'groupsetsBar') return null;
                if (profile && ['groupsetsLeft', 'groupsetsMiddle', 'groupsetsBar', 'groupsetsGear'].includes(key)) return null;
                rot = GROUPSET_ANGLE[key];
                return {
                  ...props,
                  type: key,
                  anchor: key,
                  index: CONSTANTS[ key ].zIndex,
                  scale: CONSTANTS[ key ].scale || props.scale,
                  image: color.color[ GRUOPSET_ANCHORS[key] ],
                  origin: key === 'groupsetsMiddle' ? props.origin : {},
                  rotation: rot ? radians( framesetProps[rot] || 0 ) : 0
                }
              }).filter(Boolean).map(layer => calibrateCatalogueLayer(layer, calibration, framesetProps.wheelScale || 1));
              return matched.concat(legacy, leverLayer(props, color.color.imageBar, framesetProps.wheelScale || 1));
            }

            case 'cages':

              // One cage on the seat tube, one on the down tube; each has its
              // own frameset anchor and a rotation aligning it to the tube.
              // cage_scale (per frameset+cage) resizes both cages uniformly.
              props.scale *= cageProps.cageScale || 1;
              return [ 'Seat', 'Down' ].map( pos => {
                // Mirroring is stored per cage (cage_seat_flip/cage_down_flip);
                // when unset, keep the historical default: down-tube cage
                // mirrored so the mounting plate faces the tube, seat cage not.
                const flip = cageProps[ 'cage' + pos + 'Flip' ];
                return {
                  ...props,
                  type: a.item.type + pos,
                  anchor: a.item.type + pos,
                  rotation: radians( cageProps[ 'cage' + pos + 'Angle' ] || 0 ),
                  flipX: flip == null ? pos === 'Down' : !! flip
                };
              });

            case 'pedals':

              // The drive-side pedal hangs on the crank's pedal eye and stays
              // level while the crank turns. Uncalibrated cranks get no pedal.
              if ( ! itemAnchors.pedals ) return;
              props.scale *= framesetProps.wheelScale || 1;
              props.followsCrank = true;

              break;

            case 'wheels':
            case 'tyres':

              // User Request: Tyre needs to scale in the same proportion as the wheel
              // So, use 'wheelScale' for both 'wheels' and 'tyres'
              props.scale *= framesetProps.wheelScale || 1;
              return [ 'Left', 'Right' ].map( pos => ({
                ...props,
                type: a.item.type + pos,
                anchor: a.item.type + pos
              }));

            case 'saddles':

              props.blockRotation = true;

              break;

            case 'seatposts':

              itemProps = this.itemProps( a );
              props.rotation = radians( framesetProps.inclinationRear || 0 );
              props.scale = framesetProps.seatpostScale || props.scale;

              itemAnchors.saddles = {
                x: itemProps.saddleX || 0,
                y: itemProps.saddleY || 0,
                type: 'seatposts'
              };

              break;
          }

          return props;
        })
        .filter( a => a )
        .flat();

        // RESULT

        return {
          frameset,
          anchors,
          itemAnchors,
          items,
          chain
        };
      }
    },

  methods: {
      // Resolve the cage geometry for the cage that is actually selected.
      // Precedence: frameset defaults < this (frameset, cage) < that pair for
      // this colour. Falling back to the frameset keeps framesets that have no
      // FramesetCage row yet rendering exactly as before.
      cageProps( frameset, framesetProps ) {

        const cage = this.items.find( a => a.item && a.item.type === 'cages' );
        if ( ! cage || ! this.cageGeometry ) return framesetProps;

        const colorRow = frameset.item.colors[ frameset.color ];
        const colorId = colorRow && colorRow.color && colorRow.color.id;

        const rows = this.cageGeometry.filter( g =>
          g.idFrameset === frameset.item.id && g.idCage === cage.item.id );

        const props = { ...framesetProps };
        // Model row first, then the colour tweak on top of it.
        [ rows.find( g => g.idFramesetColor == null ),
          rows.find( g => g.idFramesetColor === colorId ) ].forEach( row => {
          if ( ! row ) return;
          CAGE_FIELDS.forEach( field => {
            if ( row[ field ] != null ) props[ field ] = row[ field ];
          });
        });

        return props;
      },

      itemProps( comp ) {

        const props = { ...comp.item };
        const selected = comp.item.colors[ comp.color ];

        if ( selected ) {
          const excludeKeys = ['id','color','color2','colorName','image','price','priority'];
          for ( var key in selected.color ) {
            if ( excludeKeys.indexOf( key ) < 0 ) {
              if ( selected.color[key] != null ) {
                props[key] = selected.color[key];
              }
            }
          }
        }

        return props;
      },
      resizeCanvas() {
        this.getContext();
        this.onResize();
        this.mountBike();
      },
      getContext() {
        const { canvas } = this.$refs;
        if ( canvas ) {
          // Render the backing store at device resolution: the canvas is
          // CSS-scaled to fit the container, and a 1x store upscaled on a
          // retina screen shows jagged edges. Drawing code stays in logical
          // (width x height) units via the transform.
          const dpr = ( window.devicePixelRatio || 1 );
          canvas.width  = this.width * dpr;
          canvas.height = this.height * dpr;
          this.context  = canvas.getContext('2d');
          this.context.setTransform( dpr, 0, 0, dpr, 0, 0 );
          this.context.imageSmoothingEnabled = true;
          this.context.imageSmoothingQuality = 'high';
        }
      },
      mountBike() {
        const version = ++this.renderVersion;
        this.failedGroupset = false;
        const nextBuffer = [];
        const source = this.composition;
        const stagedItems = source && source.items.map(item => ({ ...item, origin: { ...item.origin } }));
        const composition = source && { ...source, items: stagedItems, frameset: stagedItems[source.items.indexOf(source.frameset)] };
        if ( composition ) {

          var waiting = [];
          const { frameset } = composition;
          frameset.loaded = false;

          const loading = composition.items.map( item => {
            // Optional layers may contain only a newline in the original catalogue.
            item.image = (item.image || '').trim();
            return item.image && loadImage(item.image).then( image => {
              if (version !== this.renderVersion) return;
              
              if (!image) {
                  if (item.type.startsWith('groupsets')) this.failedGroupset = true;
                  console.error('Bike: LoadImage failed for item', item.type, item.image);
                  return; 
              }
              console.log('Bike: LoadImage success', item.type);

              const anchor = composition.itemAnchors[ item.anchor ] || composition.anchors[ item.anchor ];
              let origin = item.origin;
              let width    = item.renderWidth || image.naturalWidth; // changed from const for resizing
              let height   = item.renderHeight || image.naturalHeight;

              // Scale down huge custom images
              if ( item.image.startsWith('data:') && width > 740 ) {
                const ratio = 740 / width;
                width *= ratio;
                height *= ratio;
              }

              if (/^(wheels|tyres)(Left|Right)$/.test(item.type)) {
                origin = wheelArtworkOrigin(item.image, origin, width, height);
                item.outerRadius = wheelArtworkRadius(item.image, width, height);
              }

              if ( origin.x == null ) origin.x = width / 2;
              if ( origin.y == null ) origin.y = height / 2;
              if ( item.anchor === 'framesets' ) origin.y += 60 * item.scale; // Reajust to centrate

              if ( item.anchor === 'framesets' ) {

                const imgScale = item.imageScale || item.scale;
                // baseX/baseY: used by other components for anchor positioning (always uses base scale)
                frameset.baseX = this.width / 2 - origin.x * item.scale;
                frameset.baseY = this.height / 2 - origin.y * item.scale;

                nextBuffer.push( Object.assign( frameset, {
                  image,
                  origin: { x: 0, y: 0 },
                  width: width * imgScale,
                  height: height * imgScale,
                  x: this.width / 2 - origin.x * imgScale,
                  y: this.height / 2 - origin.y * imgScale,
                  loaded: true
                }));

              } else {

                waiting.push( Object.assign( item, {
                  width,  height, image,
                  anchor, origin, source: item.image
                }));
              }

              if ( frameset.loaded ) {

                // Items anchored to another item (e.g. the shifter on the bar) can
                // only mount after their parent is in the buffer. A single pass can
                // strand them when the parent mounts later in the same pass, so
                // keep re-processing until no more items resolve.
                let before;
                do {
                  before = waiting.length;
                  waiting = waiting.map( item => {

                    if ( item.anchor.type && ( item.parent = composition.items.find( a => a.type === item.anchor.type ))) {
                      if ( nextBuffer.indexOf( item.parent ) < 0 ) return item;

                      if ( item.type === 'groupsetsBar' ) this.seatLever( item, item.parent,
                        item.parent.scale / frameset.scale, item.parent.width / item.parent.scale, item.parent.height / item.parent.scale );
                      item.scale  *= frameset.scale;
                      item.width  *= item.scale;
                      item.height *= item.scale;

                      nextBuffer.push( item );
                      return;
                    }

                    item.parent = null;
                    if ( item.type === 'groupsetsBar' ) {
                      const frameScale = frameset.imageScale || frameset.scale;
                      const w = frameset.width / frameScale, h = frameset.height / frameScale;
                      // Integrated cockpit: search only around the bar, never the fork.
                      this.seatLever( item, frameset, 1, w, h, { up: .2 * h, down: .2 * h, back: .15 * w } );
                    }
                    item.scale  *= frameset.scale;
                    item.width  *= item.scale;
                    item.height *= item.scale;
                    item.x = frameset.baseX + item.anchor.x * frameset.scale;
                    item.y = frameset.baseY + item.anchor.y * frameset.scale;
                    nextBuffer.push( item );
                  })
                  .filter( a => a );
                } while ( waiting.length < before );
              }
            })
          });
          // Keep the previous complete bicycle visible until every new layer settles.
          return Promise.all(loading).then(() => {
            if (version === this.renderVersion) this.buffer = nextBuffer;
          });
        } else {
          this.buffer = [];
        }
      },
      onBuffer( next, prev ) {
        if ( next !== prev ) this.$emit( 'buffer' );
        if ( next !== prev ) {
          this.hover = null;
          if ( this.interactive ) {
            if ( !this.views.some( v => v.id === this.view )) this.view = 'full';
            this.camRect = this.view === 'full' ? null : this.viewRect( this.view );
            this.playChanges( next, prev );
          }
        }
        this.draw();
      },
      stageMatrices() {
        const fit = wheelViewportFit(this.buffer, this.width, this.height);
        const level = wheelLevel(this.buffer);
        const fitM = new DOMMatrix().translateSelf(fit.x, fit.y).scaleSelf(fit.scale, fit.scale);
        const levelM = new DOMMatrix();
        if (level) levelM.translateSelf(level.x, level.y).rotateSelf(level.angle * 180 / Math.PI).translateSelf(-level.x, -level.y);
        return { fit: fitM, level: levelM };
      },
      cameraMatrix() {
        const r = this.camRect;
        if ( !r ) return null;
        const k = this.width / r.w;
        return new DOMMatrix().scaleSelf(k, k).translateSelf(-r.x, -r.y);
      },
      // Canvas-logical transform of a layer, without the camera.
      stageMatrix( item ) {
        const { fit, level } = this.stageMatrices();
        return fit.multiply(level).multiply(layerMatrix(item, this.motion));
      },
      chainShift() {
        const ride = this.motion.ride;
        return ride ? ride.chain : 0;
      },
      viewRect( id ) {
        const view = VIEWS.find( v => v.id === id );
        const parts = view && view.parts ? this.buffer.filter( item => view.parts.test( item.type )) : [];
        if ( !parts.length ) return null;
        const boxes = parts.map( item => boundsOf( this.stageMatrix( item ), layerBox( item )));
        const union = boxes.reduce(( a, b ) => ({
          left: Math.min( a.left, b.left ), top: Math.min( a.top, b.top ),
          right: Math.max( a.right, b.right ), bottom: Math.max( a.bottom, b.bottom )
        }));
        return frameRect( union, this.width, this.height, view.pad );
      },
      setView( id ) {
        if ( id === this.view ) return;
        const full = { x: 0, y: 0, w: this.width, h: this.height };
        const from = this.camRect || full;
        const to = id === 'full' ? full : this.viewRect( id );
        if ( !to ) return;
        this.view = id;
        this.hover = null;
        this.addTrack({ delay: 0, dur: this.reducedMotion() ? 1 : 650, step: p => {
          this.camRect = p >= 1 && id === 'full' ? null : lerpRect( from, to, ease.inOut3( p ));
        }});
      },
      reducedMotion() {
        return !!( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches );
      },
      // Tiny timeline: each track gets eased progress until its duration ends.
      addTrack( track ) {
        // The clock starts on the first frame, so a slow first draw never
        // swallows the animation.
        track.start = null;
        this.tracks.push( track );
        if ( !this.frameId ) this.frameId = requestAnimationFrame( this.tick );
      },
      tick( now ) {
        this.frameId = null;
        const running = [];
        this.tracks.forEach( track => {
          if ( track.start == null ) track.start = now + ( track.delay || 0 );
          const p = clamp(( now - track.start ) / track.dur );
          if ( now >= track.start ) track.step( p, now );
          if ( p < 1 ) running.push( track ); else if ( track.done ) track.done();
        });
        this.tracks = running;
        this.draw();
        if ( running.length ) this.frameId = requestAnimationFrame( this.tick );
      },
      // Animate the layers whose artwork changed: wheels roll in, cranks spin,
      // cockpit and saddle drop into place, everything else fades.
      playChanges( next, prev ) {
        if ( this.reducedMotion() ) return;
        const before = new Map(( prev || [] ).map( item => [ item.type, item.image && item.image.src ]));
        const changed = next.filter( item => before.get( item.type ) !== ( item.image && item.image.src ));
        if ( !changed.length ) return;
        const all = !prev || !prev.length || changed.some( item => item.type === 'framesets' );
        const moved = new Set(( all ? next : changed ).map( item => item.type ));
        // A new tyre or wheel turns the whole wheel unit of that side together.
        [ 'Left', 'Right' ].forEach( side => {
          if ( [ 'wheels', 'tyres' ].some( k => moved.has( k + side ))) {
            [ 'wheels' + side, 'tyres' + side, 'groupsetsBrake' + side ].forEach( t => moved.add( t ));
          }
        });
        const drive = [ ...moved ].some( t => /^groupsets(Middle|Left)$/.test( t ));
        next.forEach( item => {
          if ( !moved.has( item.type )) return;
          const type = item.type;
          const set = values => { this.motion[ type ] = values; };
          const done = () => { delete this.motion[ type ]; };
          set({ alpha: 0 });
          if ( wheelPart( item )) {
            const delay = all ? ( /Left$/.test( type ) ? 60 : 120 ) : 0;
            this.addTrack({ delay, dur: 720, done, step: p => set({
              spin: -4.2 * ( 1 - ease.out3( p )), scale: .82 + .18 * ease.spring( p ), alpha: clamp( p * 5 ) }) });
          } else if ( crankPart( item )) {
            this.addTrack({ delay: all ? 340 : 60, dur: 680, done, step: p => set({
              spin: -3.4 * ( 1 - ease.out3( p )), alpha: clamp( p * 5 ) }) });
          } else if ( /^(bars|saddles|seatposts|cages|groupsetsBar)/.test( type )) {
            const delay = all ? { seatposts: 380, saddles: 480, bars: 420, groupsetsBar: 420 }[ type ] || 560 : 0;
            this.addTrack({ delay, dur: 620, done, step: p => set({
              dy: -46 * ( 1 - ease.spring( p )), alpha: clamp( p * 6 ) }) });
          } else {
            this.addTrack({ delay: all ? ( type === 'framesets' ? 0 : 260 ) : 120, dur: 320, done, step: p => set({
              alpha: ease.out3( p ), dy: type === 'framesets' ? 14 * ( 1 - ease.out3( p )) : 0 }) });
          }
        });
        if ( drive && !all ) setTimeout(() => this.ride({ revs: 1, dur: 1100, gauge: false }), 760 );
      },
      ride({ revs = 3, dur = 2600, gauge = true } = {}) {
        if ( this.riding || !this.buffer.length ) return;
        const front = this.buffer.find( item => item.type === 'groupsetsMiddle' );
        const total = revs * Math.PI * 2;
        const glide = gauge ? 1500 : 700;
        const chainRadius = this.chainRadius( front );
        // Wheels and chain stay where the last ride left them.
        const rest = this.motion.ride || { crank: 0, wheel: 0, chain: 0 };
        // Freewheel: the wheel follows the cranks but never slows faster
        // than it would coasting, so it keeps rolling after the cranks stop.
        let wheel = rest.wheel, spin = 0, last = null, kmh = 0, rpm = 0;
        this.riding = true;
        if ( gauge ) this.speedo = { kmh: 0, rpm: 0, arc: 0, needle: -90 };
        this.addTrack({ delay: 0, dur: dur + glide, step: ( p, now ) => {
          const ms = p * ( dur + glide );
          const crank = total * ease.inOutSine( clamp( ms / dur ));
          if ( last && now > last.now ) {
            const dt = ( now - last.now ) / 1000;
            const cadence = ( crank - last.crank ) / dt;
            spin = Math.max( cadence * GEAR_RATIO, spin * Math.exp( -dt / .45 ));
            wheel += spin * dt;
            if ( gauge ) {
              kmh += ( spin * WHEEL_RADIUS_M * 3.6 - kmh ) * .35;
              rpm += ( cadence * 60 / ( Math.PI * 2 ) - rpm ) * .35;
              const f = clamp( kmh / 50 );
              this.speedo = { kmh: Math.round( kmh ), rpm: Math.round( rpm ), arc: ( f * 100 ).toFixed( 1 ), needle: ( -90 + 180 * f ).toFixed( 1 ) };
            }
          }
          this.motion.ride = { crank, wheel, chain: rest.chain + crank * chainRadius };
          last = { now, crank };
        }, done: () => {
          // Full crank turns end where they began; the wheel rests wherever it rolled to.
          this.motion.ride = { crank: 0, wheel: wheel % ( Math.PI * 2 ), chain: rest.chain + total * chainRadius };
          this.riding = false;
          if ( gauge ) setTimeout(() => { if ( !this.riding ) this.speedo = null; }, 900 );
        }});
      },
      chainRadius( front ) {
        if ( !front ) return 0;
        if ( front.calibration ) return front.calibration.front.radius * front.scale;
        if ( front.drivetrain && front.drivetrain.chain ) return front.drivetrain.chain.ring * front.scale;
        if ( front.drivetrain ) return ( front.drivetrain.front.origin.y - front.drivetrain.front.top ) * front.scale;
        const chain = this.composition && this.composition.chain;
        return chain && chain.front ? chain.front * front.scale / 2 : 0;
      },
      // Topmost part under a canvas-logical point. Wheels count as solid discs
      // so the spokes' gaps still select them.
      partAt( point ) {
        const cam = this.cameraMatrix();
        const sorted = this.buffer.slice().sort(( a, b ) => b.index - a.index );
        for ( const item of sorted ) {
          if ( !item.image || !partStep( item.type )) continue;
          let m = this.stageMatrix( item );
          if ( cam ) m = cam.multiply( m );
          const local = m.inverse().transformPoint( new DOMPoint( point.x, point.y ));
          if ( /^wheels(Left|Right)$/.test( item.type )) {
            const r = ( item.outerRadius || Math.max( item.width, item.height ) / item.scale / 2 ) * item.scale;
            if ( Math.hypot( local.x - item.origin.x * item.scale, local.y - item.origin.y * item.scale ) <= r ) return { item, m };
          } else if ( opaqueAt( item, local.x, local.y )) {
            return { item, m };
          }
        }
        return null;
      },
      canvasPoint( e ) {
        const rect = this.$refs.canvas.getBoundingClientRect();
        return { x: ( e.clientX - rect.left ) * this.width / rect.width, y: ( e.clientY - rect.top ) * this.height / rect.height };
      },
      handlePointer( e ) {
        if ( !this.interactive || this.editable || this.cageEditMode || this.dragging ) return;
        const point = this.canvasPoint( e );
        cancelAnimationFrame( this.pointerFrame );
        this.pointerFrame = requestAnimationFrame(() => this.updateHover( point ));
      },
      updateHover( point ) {
        const hit = this.partAt( point );
        const step = hit && partStep( hit.item.type );
        const entry = step && this.items.find( a => a.item && a.item.type === step );
        if ( !entry ) { this.leaveHover(); return; }
        clearTimeout( this.hoverTimer );
        const { canvas, container } = this.$refs;
        const cr = canvas.getBoundingClientRect(), pr = container.getBoundingClientRect();
        const k = cr.width / this.width, ox = cr.left - pr.left, oy = cr.top - pr.top;
        const b = boundsOf( hit.m, layerBox( hit.item ));
        const color = entry.item.colors[ entry.color ] || {};
        const price = ( color.color && color.color.price ) || color.price || 0;
        // Hubs, cranks and other round parts get a ring around the axle;
        // big shapes like the frame show the label next to the pointer.
        const around = wheelPart( hit.item ) || crankPart( hit.item );
        const centre = around ? hit.m.transformPoint( new DOMPoint( hit.item.origin.x * hit.item.scale, hit.item.origin.y * hit.item.scale ))
          : { x: ( b.left + b.right ) / 2, y: ( b.top + b.bottom ) / 2 };
        const rolling = /^(wheels|tyres)(Left|Right)$/.test( hit.item.type );
        const wide = !rolling && ( step === 'framesets' || ( b.right - b.left ) * k > 260 );
        const at = wide ? point : centre;
        const radius = rolling ? this.wheelRingRadius( hit.item ) * k + 10
          : clamp( Math.max( b.right - b.left, b.bottom - b.top ) * k / 2 + 6, 26, 64 );
        const x = ox + at.x * k, y = oy + at.y * k;
        const gap = wide ? 18 : radius + 10;
        const side = pr.width - x >= x ? 'right' : 'left';
        const room = Math.max( 120, ( side === 'right' ? pr.width - x : x ) - gap - 12 );
        this.hover = {
          step,
          key: hit.item.type,
          label: PART_LABELS[ step ],
          name: entry.item.name,
          price: euros( Number( price ) ),
          side,
          ring: wide ? null : { left: `${ x - radius }px`, top: `${ y - radius }px`, width: `${ radius * 2 }px`, height: `${ radius * 2 }px` },
          tip: { top: `${ y }px`, maxWidth: `${ room }px`,
            ...( side === 'right' ? { left: `${ x + gap }px` } : { right: `${ pr.width - x + gap }px` }) }
        };
      },
      // Screen-space radius (canvas units) of the whole wheel, tyre included.
      // The given layers alone, at rest, as the bike draws them: the wheel
      // spotlight uses the front wheel under its tyre (the catalogue wheel
      // images are cropped at the rim; on the bike the tyre covers that).
      partSnapshot( types, size = 720 ) {
        const items = this.buffer.filter( item => types.includes( item.type ) && item.image ).sort(( a, b ) => a.index - b.index );
        if ( !items.length ) return '';
        const { fit, level } = this.stageMatrices();
        const base = fit.multiply( level );
        const mats = items.map( item => base.multiply( layerMatrix( item, {} )));
        const box = items.map(( item, i ) => boundsOf( mats[ i ], layerBox( item ))).reduce(( a, b ) => ({
          left: Math.min( a.left, b.left ), top: Math.min( a.top, b.top ),
          right: Math.max( a.right, b.right ), bottom: Math.max( a.bottom, b.bottom )
        }));
        const w = box.right - box.left, h = box.bottom - box.top, k = size / Math.max( w, h );
        const canvas = document.createElement( 'canvas' );
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext( '2d' );
        ctx.translate(( size - w * k ) / 2, ( size - h * k ) / 2 );
        ctx.scale( k, k );
        ctx.translate( -box.left, -box.top );
        items.forEach(( item, i ) => {
          const m = mats[ i ];
          ctx.save();
          ctx.transform( m.a, m.b, m.c, m.d, m.e, m.f );
          ctx.drawImage( item.image, 0, 0, item.width, item.height );
          ctx.restore();
        });
        try {
          // Trim the layers' transparent margins so the part fills the picture
          // and labels set against its edge sit on the tyre, not over it.
          const data = ctx.getImageData( 0, 0, size, size ).data;
          let x0 = size, y0 = size, x1 = -1, y1 = -1;
          for ( let y = 0; y < size; y += 2 ) for ( let x = 0; x < size; x += 2 ) {
            if ( data[( y * size + x ) * 4 + 3 ] > 16 ) {
              if ( x < x0 ) x0 = x; if ( x > x1 ) x1 = x; if ( y < y0 ) y0 = y; if ( y > y1 ) y1 = y;
            }
          }
          if ( x1 < 0 ) return '';
          const side = Math.max( x1 - x0, y1 - y0 ) + 4;
          const trimmed = document.createElement( 'canvas' );
          trimmed.width = trimmed.height = side;
          trimmed.getContext( '2d' ).drawImage( canvas, x0 - ( side - ( x1 - x0 )) / 2, y0 - ( side - ( y1 - y0 )) / 2, side, side, 0, 0, side, side );
          return trimmed.toDataURL( 'image/png' );
        } catch ( e ) { return ''; }
      },
      wheelRingRadius( item ) {
        const side = /Left$/.test( item.type ) ? 'Left' : 'Right';
        return this.layerRadius( this.buffer.find( part => part.type === 'tyres' + side ) || item );
      },
      // Each wheel's centre and rim radius in container pixels (rim-depth callout).
      wheelMarks() {
        const { canvas, container } = this.$refs;
        if ( !canvas || !container || !this.buffer.length ) return [];
        const cr = canvas.getBoundingClientRect(), pr = container.getBoundingClientRect();
        const k = cr.width / this.width, ox = cr.left - pr.left, oy = cr.top - pr.top;
        const cam = this.cameraMatrix();
        return [ 'Left', 'Right' ].map( side => {
          const wheel = this.buffer.find( part => part.type === 'wheels' + side );
          if ( !wheel ) return null;
          let m = this.stageMatrix( wheel );
          if ( cam ) m = cam.multiply( m );
          const c = m.transformPoint( new DOMPoint( wheel.origin.x * wheel.scale, wheel.origin.y * wheel.scale ));
          return { side, x: ox + c.x * k, y: oy + c.y * k, r: this.layerRadius( wheel ) * k };
        }).filter( Boolean );
      },
      layerRadius( ring ) {
        const cam = this.cameraMatrix();
        let m = this.stageMatrix( ring );
        if ( cam ) m = cam.multiply( m );
        const ox = ring.origin.x * ring.scale, oy = ring.origin.y * ring.scale;
        const r = ( ring.outerRadius || Math.max( ring.width, ring.height ) / ring.scale / 2 ) * ring.scale;
        const a = m.transformPoint( new DOMPoint( ox, oy )), b = m.transformPoint( new DOMPoint( ox + r, oy ));
        return Math.hypot( b.x - a.x, b.y - a.y );
      },
      clearHover() {
        cancelAnimationFrame( this.pointerFrame );
        clearTimeout( this.hoverTimer );
        this.hover = null;
      },
      // Leaving a part keeps its label a moment, so the pointer can reach "Change".
      leaveHover() {
        cancelAnimationFrame( this.pointerFrame );
        clearTimeout( this.hoverTimer );
        if ( this.hover ) this.hoverTimer = setTimeout(() => { this.hover = null; }, 350 );
      },
      keepHover() {
        clearTimeout( this.hoverTimer );
      },
      chooseHover() {
        const step = this.hover && this.hover.step;
        this.clearHover();
        if ( step ) this.$emit( 'select-part', step );
      },
      handleClick( e ) {
        if ( !this.interactive || this.editable || this.cageEditMode ) return;
        const hit = this.partAt( this.canvasPoint( e ));
        const step = hit && partStep( hit.item.type );
        if ( step ) {
          this.hover = null;
          this.$emit( 'select-part', step );
        }
      },
      // Size and seat the lever on its host bar (separate bar or integrated
      // cockpit) from the artwork itself; keeps the calibration on failure.
      seatLever( item, host, hostScale, width, height, roi = null ) {
        const landmarks = leverLandmarks( item.source );
        if ( !landmarks || !host || !host.image ) return;
        const base = calibratedHoodLength( landmarks, item, item.image );
        const fit = fitLever( seatOnBar( silhouette( host.image, width, height ), item.anchor, roi ), hostScale, landmarks, base );
        if ( !fit ) return;
        item.anchor = { ...item.anchor, x: fit.anchor.x, y: fit.anchor.y };
        item.origin = fit.origin;
        item.scale = fit.scale;
        item.rotation = fit.rotation;
        item.width = item.image.naturalWidth;
        item.height = item.image.naturalHeight;
      },
      clear() {
        const { context } = this;
        context.clearRect( 0, 0, this.width, this.height );
      },
      draw() {

        //const frameset = this.buffer.find( a => a.type === 'framesets' );

        this.clear();
        let sorted = this.buffer.slice().sort(( a, b ) => a.index - b.index );
        // In moveFrameMode, draw frameset last (on top) so it's visible
        if (this.moveFrameMode) {
          const framesetIdx = sorted.findIndex(i => i.type === 'framesets');
          if (framesetIdx >= 0) {
            const [fs] = sorted.splice(framesetIdx, 1);
            sorted.push(fs);
          }
        }
        this.context.save();
        const cam = this.cameraMatrix();
        if (cam) this.context.transform(cam.a, cam.b, cam.c, cam.d, cam.e, cam.f);
        const { fit, level } = this.stageMatrices();
        this.context.transform(fit.a, fit.b, fit.c, fit.d, fit.e, fit.f);
        this.context.save();
        this.context.transform(level.a, level.b, level.c, level.d, level.e, level.f);
        sorted.forEach( item => {

          if ( item.type === 'groupsetsMiddle' )
            this.drawFrontChain( item );

          this.context.save();

          const own = this.motion[ item.type ] || ( item.parent && this.motion[ item.parent.type ] );
          if ( own && own.alpha != null ) this.context.globalAlpha = clamp( own.alpha );

          const m = layerMatrix( item, this.motion );
          this.context.transform( m.a, m.b, m.c, m.d, m.e, m.f );

          // Keep the photographic gear/chain wraps, trim only the straight tails.
          if (item.rearImageSplit) {
            // The Force XPLR catalogue sheet placed the derailleur below the
            // cassette with extra spacing. Mount it separately at the axle.
            const split = item.rearImageSplit;
            const sourceHeight = item.image.naturalHeight;
            this.context.drawImage(item.image, 0, 0, item.image.naturalWidth, split.y,
              0, 0, item.width, split.y * item.scale);
            this.context.drawImage(item.image, 0, split.y, item.image.naturalWidth, sourceHeight - split.y,
              0, (split.y + split.offsetY) * item.scale, item.width, (sourceHeight - split.y) * item.scale);
          } else if (item.cropLeft != null || item.cropRight != null) {
            const left = item.cropLeft || 0;
            const right = item.cropRight || item.image.naturalWidth;
            this.context.drawImage(item.image, left, 0, right - left, item.image.naturalHeight,
              left * item.scale, 0, (right - left) * item.scale, item.height);
          } else {
            this.context.drawImage( item.image, 0, 0, item.width, item.height );
          }
          this.context.restore();

          if ( this.cageEditMode && item.type && item.type.startsWith('cages') ) {
            const ctx = this.context;
            ctx.save();
            ctx.strokeStyle = '#e91e63';
            ctx.setLineDash([ 6, 4 ]);
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc( item.x, item.y, Math.max( item.width, item.height ) / 2 + 8, 0, Math.PI * 2 );
            ctx.stroke();
            ctx.restore();
          }

          if ( item.type === 'groupsetsLeft' )
            this.drawRearChain( item );

          // A drawn chain loop rides on the turning rings, under both derailleurs.
          if ( item.type === 'groupsetsMiddle' && item.drivetrain && item.drivetrain.chain )
            this.drawChainLoop( item );

        });
        this.context.restore();
        const frame = this.buffer.find(item => item.type === 'framesets');
        if (frame) this.drawShadow(frame);
        this.context.restore();
      },
      drawChainLoop( item ) {
        const chain = drivetrainChain(item, this.buffer.find(a => a.type === 'groupsetsLeft'));
        if (chain) drawChainLoop(this.context, loopPins(chain.loop, chain.pitch, this.chainShift()), chain.width);
      },
      drawFrontChain( item ) {
        const rear = this.buffer.find(a => a.type === 'groupsetsLeft');
        if (!rear) return;
        if (item.drivetrain) {
          if (item.drivetrain.chain) return;
          const points = chainSeams(item, rear, true);
          if (points) drawChainLinks(this.context, points, item.scale * 18, item.scale * 10, -this.chainShift());
          return;
        }
        const { chain } = this.composition;
        if (!chain || !chain.rear || !chain.front) return;
        const calibrated = catalogueChain(item, rear);
        const radius = calibrated ? calibrated.frontRadius : chain.front * item.scale / 2;
        const rearRadius = calibrated ? calibrated.rearRadius : chain.rear * rear.scale / 2;
        const frontTop = { x: item.x, y: item.y - radius };
        const rearTop = { x: rear.x, y: rear.y - rearRadius };
        const lowerRear = calibrated ? calibrated.lowerRear : {
          x: rear.x + (chain.break.x - rear.origin.x) * rear.scale,
          y: rear.y + (chain.break.y - rear.origin.y) * rear.scale
        };
        const pitch = Math.max(2, radius * .085), width = pitch * .55;
        const shift = this.chainShift();
        drawChainLinks(this.context, arcPoints(item.x, item.y, radius, -Math.PI / 2, Math.PI / 2), pitch, width, shift);
        drawChainLinks(this.context, [frontTop, { x: (frontTop.x + rearTop.x) / 2, y: (frontTop.y + rearTop.y) / 2 }], pitch, width, -shift);
        drawChainLinks(this.context, [{ x: item.x, y: item.y + radius }, lowerRear], pitch, width, shift);
      },
      drawRearChain( item ) {
        const front = this.buffer.find(a => a.type === 'groupsetsMiddle');
        if (!front) return;
        if (item.drivetrain) {
          if (item.drivetrain.chain) return;
          const points = chainSeams(front, item);
          if (points) drawChainLinks(this.context, points, front.scale * 18, front.scale * 10, this.chainShift());
          return;
        }
        const { chain } = this.composition;
        if (!chain || !chain.rear || !chain.front) return;
        const calibrated = catalogueChain(front, item);
        const radius = calibrated ? calibrated.rearRadius : chain.rear * item.scale / 2;
        const frontRadius = calibrated ? calibrated.frontRadius : chain.front * front.scale / 2;
        const rearTop = { x: item.x, y: item.y - radius };
        const frontTop = { x: front.x, y: front.y - frontRadius };
        const pitch = Math.max(2, frontRadius * .085), width = pitch * .55;
        const shift = this.chainShift();
        drawChainLinks(this.context, arcPoints(item.x, item.y, radius, -Math.PI / 2, -Math.PI * 1.5), pitch, width, -shift);
        drawChainLinks(this.context, [rearTop, { x: (frontTop.x + rearTop.x) / 2, y: (frontTop.y + rearTop.y) / 2 }], pitch, width, shift);
      },
      drawShadow( item ) {

        const ctx = this.context;

        // Calc dimensions and Position
        const wheel1 = this.composition.anchors.wheelsLeft;
        const wheel2 = this.composition.anchors.wheelsRight;
        const size = ( wheel2.x - wheel1.x ) * item.scale;
        const level = wheelLevel(this.buffer);
        const rings = this.buffer.filter(part => /^(wheels|tyres)(Left|Right)$/.test(part.type));
        const radius = Math.max(0, ...rings.map(part => (part.outerRadius || 0) * part.scale));
        const x = level ? level.x : item.x + size / 2;
        const y = level ? level.y + radius : item.y + (Math.max(wheel1.y, wheel2.y) + 215 * CONSTANTS.wheels.scale) * item.scale;
        const gradient = ctx.createRadialGradient( 0, 0, 0, 0, 0, size );

        gradient.addColorStop( 0, 'rgba(0,0,0,0.05)' );
        gradient.addColorStop( 1, 'rgba(0,0,0,0)' );

        ctx.save();
        ctx.translate( x, y );
        ctx.scale( 1, .05 ); // To draw Gradient as a Ellipse

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.ellipse( 0, 0, size, size, 0, 0, 2 * Math.PI );
        ctx.fill();

        ctx.restore(); // Reset Canvas state
      },
      onResize(e) {
        const { width, height } = this;
        const { container, canvas } = this.$refs;
        
        if (container) {
            console.log('Bike: onResize container:', container.clientWidth, 'x', container.clientHeight);
        }
        
        containImage( canvas, width, height, container, e );
      },
      // DRAG HANDLERS
      getMousePos(e) {
          const rect = this.$refs.canvas.getBoundingClientRect();
          // Scale mouse coordinates to match Canvas internal resolution (1400x1000)
          const scaleX = this.width / rect.width;
          const scaleY = this.height / rect.height;
          return unlevelPoint(unfitPoint({
              x: (e.clientX - rect.left) * scaleX,
              y: (e.clientY - rect.top) * scaleY
          }, wheelViewportFit(this.buffer, this.width, this.height)), wheelLevel(this.buffer));
      },
      handleMouseDown(e) {
          if (!this.editable && !this.cageEditMode) return;
          const pos = this.getMousePos(e);
          // console.log('Bike: MouseDown at', pos);

          // Find hit - Iterate buffer in reverse draw order (top first)
          const draggableTypes = ['wheelsLeft', 'wheelsRight', 'saddles', 'framesets'];
          const hit = this.buffer.slice().reverse().find(item => {
              if (!item.type) return false;
              // In cageEditMode, only the two cages are draggable
              if (this.cageEditMode && !item.type.startsWith('cages')) return false;
              // In moveFrameMode, only the frameset is draggable
              if (!this.cageEditMode && this.moveFrameMode) return item.type === 'framesets';
              if (!this.cageEditMode && !draggableTypes.includes(item.type) && !item.type.startsWith('groupsets')) return false;

              // Frameset: x,y is already top-left corner, no origin offset
              const ox = item.origin ? item.origin.x * item.scale : 0;
              const oy = item.origin ? item.origin.y * item.scale : 0;
              const drawX = item.x - ox;
              const drawY = item.y - oy;

              return pos.x >= drawX && pos.x <= drawX + item.width &&
                     pos.y >= drawY && pos.y <= drawY + item.height;
          });

          if (hit) {
              // console.log('Bike: Drag Start on', hit.type);
              this.dragging = hit;
              this.dragStart = pos;
              // Store initial anchor to calculate delta
              // anchor is { x, y } relative to frameset
              this.initialAnchor = { ...hit.anchor };
          }
      },
      handleMouseMove(e) {
        if (!this.dragging) return;
        const pos = this.getMousePos(e);
        const dx = pos.x - this.dragStart.x;
        const dy = pos.y - this.dragStart.y;

        // Find frameset reference to calculate scale
        const frameset = this.buffer.find(b => b.type === 'framesets');
        if (!frameset) return;

        if (this.dragging.type === 'framesets') {
            // Move frameset only (reposition relative to other components)
            this.dragging.x += dx;
            this.dragging.y += dy;
            this.dragStart = pos;
            if (!this.moveFrameMode) {
                // Normal mode: move everything together
                frameset.baseX += dx;
                frameset.baseY += dy;
                this.buffer.forEach(item => {
                    if (item !== this.dragging) {
                        item.x += dx;
                        item.y += dy;
                    }
                });
            }
            // Reverse-calculate new origin from the image position
            const imgScale = frameset.imageScale || frameset.scale;
            const newOriginX = (this.width / 2 - this.dragging.x) / imgScale;
            const newOriginY = (this.height / 2 - this.dragging.y) / imgScale;
            this.$emit('update-anchor', {
                anchor: 'framesets',
                x: Math.round(newOriginX),
                y: Math.round(newOriginY)
            });
        } else {
            // 1. VISUAL UPDATE
            this.dragging.x += dx;
            this.dragging.y += dy;
            this.dragStart = pos;

            // 2. LOGICAL UPDATE (Calculate Anchor relative to base position)
            const newAnchorX = (this.dragging.x - frameset.baseX) / frameset.scale;
            const newAnchorY = (this.dragging.y - frameset.baseY) / frameset.scale;

            this.$emit('update-anchor', {
                anchor: this.dragging.anchor.type || this.dragging.type,
                x: newAnchorX,
                y: newAnchorY
            });
        }
      },
      handleMouseUp() {
          this.dragging = null;
          this.lastMouseX = null;
          this.lastMouseY = null;
      },
      handleWheel(e) {
          if (!this.cageEditMode) return;
          const pos = this.getMousePos(e);
          const cage = this.buffer.slice().reverse().find(item => {
              if (!item.type || !item.type.startsWith('cages')) return false;
              const ox = item.origin ? item.origin.x * item.scale : 0;
              const oy = item.origin ? item.origin.y * item.scale : 0;
              return pos.x >= item.x - ox && pos.x <= item.x - ox + item.width &&
                     pos.y >= item.y - oy && pos.y <= item.y - oy + item.height;
          });
          if (!cage) return;
          e.preventDefault();

          if (e.shiftKey) {
              // Shift+scroll: resize BOTH cages uniformly (one cage_scale field).
              // With Shift held, browsers report the wheel on deltaX instead of
              // deltaY — read whichever axis carries the movement, or shrinking
              // is impossible.
              const delta = e.deltaY || e.deltaX;
              const f = delta > 0 ? 1 / 1.02 : 1.02;
              this.buffer.forEach(item => {
                  if (!item.type || !item.type.startsWith('cages')) return;
                  item.scale  *= f;
                  item.width  *= f;
                  item.height *= f;
              });
              const frameset = this.items[0];
              const base = (this.itemProps(frameset).cageScale || 1);
              this.liveCageScale = (this.liveCageScale || base) * f;
              this.$emit('update-cage-scale', {
                  scale: Math.round(this.liveCageScale * 1000) / 1000
              });
              return;
          }

          // stored angle is degrees, positive = CCW lean (drawn with rotate(-radians))
          cage.rotation = (cage.rotation || 0) + radians(e.deltaY > 0 ? -1 : 1);
          this.$emit('update-cage-angle', {
              anchor: cage.type,
              angle: Math.round(cage.rotation * 180 / Math.PI)
          });
      },
      // Current on-screen state of the two cages, in frameset-image coordinates.
      // Save reads THIS instead of accumulated edit deltas: the canvas keeps
      // live positions across editor toggles while pending deltas are reset,
      // so saving deltas can silently save nothing.
      getCageState() {
        const frameset = this.buffer.find( b => b.type === 'framesets' );
        const seat = this.buffer.find( b => b.type === 'cagesSeat' );
        const down = this.buffer.find( b => b.type === 'cagesDown' );
        if ( ! frameset || ! seat || ! down ) return null;
        const base = CONSTANTS.cages.scale * frameset.scale;
        return {
          cage_seat_x: Math.round(( seat.x - frameset.baseX ) / frameset.scale ),
          cage_seat_y: Math.round(( seat.y - frameset.baseY ) / frameset.scale ),
          cage_seat_angle: Math.round(( seat.rotation || 0 ) * 180 / Math.PI ),
          cage_seat_flip: seat.flipX ? 1 : 0,
          cage_down_x: Math.round(( down.x - frameset.baseX ) / frameset.scale ),
          cage_down_y: Math.round(( down.y - frameset.baseY ) / frameset.scale ),
          cage_down_angle: Math.round(( down.rotation || 0 ) * 180 / Math.PI ),
          cage_down_flip: down.flipX ? 1 : 0,
          cage_scale: Math.round( seat.scale / base * 1000 ) / 1000
        };
      },
      handleDblClick(e) {
          if (!this.cageEditMode) return;
          const pos = this.getMousePos(e);
          const cage = this.buffer.slice().reverse().find(item => {
              if (!item.type || !item.type.startsWith('cages')) return false;
              const ox = item.origin ? item.origin.x * item.scale : 0;
              const oy = item.origin ? item.origin.y * item.scale : 0;
              return pos.x >= item.x - ox && pos.x <= item.x - ox + item.width &&
                     pos.y >= item.y - oy && pos.y <= item.y - oy + item.height;
          });
          if (!cage) return;
          e.preventDefault();
          // Mirror this cage horizontally (persisted per cage as cage_*_flip)
          cage.flipX = !cage.flipX;
          this.$emit('update-cage-flip', {
              anchor: cage.type,
              flip: cage.flipX ? 1 : 0
          });
      }
    }
  }
</script>

<style>
  .bike-canvas {
    display: flex;
    justify-content: center;
    align-items: center;
  }
</style>

<style scoped>
.bike-canvas.is-interactive { position: relative; isolation: isolate; }
.bike-canvas.is-interactive > canvas { position: relative; z-index: 1; }
.bike-canvas.is-pointing > canvas { cursor: pointer; }
/* Oversized brand wordmark behind the bike, tinted with the builder accent and cropped by the stage edges. */
.bike-stage-mark { position: absolute; z-index: 0; inset: 0; background: var(--builder-accent, #344b35); opacity: .055; -webkit-mask-size: 150% auto; mask-size: 150% auto; -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat; -webkit-mask-position: center 47%; mask-position: center 47%; pointer-events: none; }
/* Hotspot ring: draws in, then the dashes march round while a soft pulse breathes out. */
.bike-hotspot-ring { position: absolute; z-index: 2; overflow: visible; pointer-events: none; animation: bike-ring-in .3s cubic-bezier(.2, .9, .3, 1.2) both; }
.bike-hotspot-ring circle { fill: none; stroke: #4f6b4f; vector-effect: non-scaling-stroke; }
.bike-hotspot-dash { stroke-width: 2.5; stroke-linecap: round; stroke-dasharray: 3.2 2; animation: bike-ring-draw .45s ease-out both, bike-ring-march 1.4s linear .45s infinite; }
.bike-hotspot-pulse { stroke-width: 2; opacity: 0; transform-box: fill-box; transform-origin: center; animation: bike-ring-pulse 1.8s ease-out .3s infinite; }
/* Two rows so the full product name always fits: label + price, then name + action. */
.builder-bike.is-spotlight .bike-hotspot-tip, .builder-bike.is-spotlight .bike-hotspot-ring { display: none; }
.bike-hotspot-tip { cursor: pointer; position: absolute; z-index: 3; display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: baseline; gap: 4px 18px; width: max-content; max-width: 340px; padding: 10px 14px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 8px; background: #fff; box-shadow: 0 6px 22px rgba(37, 44, 34, .12); color: var(--builder-ink, #252c22); font-size: 13px; line-height: 1.25; transform: translateY(-50%); animation: bike-tip-in .18s ease-out both; }
.bike-hotspot-tip strong { font-weight: 700; }
.bike-hotspot-price { justify-self: end; font-weight: 700; color: var(--builder-accent, #344b35); font-variant-numeric: tabular-nums; white-space: nowrap; }
.bike-hotspot-name { color: #62695e; overflow-wrap: anywhere; }
.bike-hotspot-cta { justify-self: end; color: #62695e; white-space: nowrap; }
.bike-stage-tools { position: absolute; z-index: 4; top: 12px; right: 12px; display: flex; align-items: center; gap: 10px; }
.bike-views { display: flex; padding: 3px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 8px; background: rgba(255, 255, 255, .85); backdrop-filter: blur(6px); }
.bike-views button, .bike-ride { min-height: 32px; padding: 0 12px; border-radius: 6px; color: #62695e; font-size: 12px; letter-spacing: .02em; transition: background-color .15s ease, color .15s ease; }
.bike-views button:hover { color: var(--builder-ink, #252c22); }
.bike-views button.is-active { background: #ecebe4; color: var(--builder-ink, #252c22); font-weight: 600; }
.bike-ride { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--builder-line, #dedfd7); background: rgba(255, 255, 255, .85); color: var(--builder-ink, #252c22); }
.bike-ride:hover:not(:disabled) { background: #fff; }
.bike-ride:disabled { cursor: default; }
.bike-ride svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; }
.bike-ride.is-riding svg { animation: bike-ring-turn .7s linear infinite; }
.bike-views button:focus-visible, .bike-ride:focus-visible { outline: 2px solid #718765; outline-offset: 2px; }
/* Bottom left: the top left holds the build presets on the frameset step. */
.bike-speedo { position: absolute; z-index: 4; bottom: 12px; left: 12px; display: flex; align-items: center; gap: 10px; padding: 8px 14px 8px 10px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 10px; background: rgba(255, 255, 255, .92); color: var(--builder-ink, #252c22); }
.bike-speedo svg { width: 76px; height: 44px; fill: none; stroke-linecap: round; }
.bike-speedo-track { stroke: #e3e2db; stroke-width: 7; }
.bike-speedo-value { stroke: var(--builder-accent, #344b35); stroke-width: 7; }
.bike-speedo-needle { stroke: var(--builder-ink, #252c22); stroke-width: 2.5; }
.bike-speedo div { display: grid; grid-template-columns: auto auto; align-items: baseline; column-gap: 4px; }
.bike-speedo strong { font-size: 26px; line-height: 1; font-weight: 700; font-variant-numeric: tabular-nums; min-width: 2ch; text-align: right; }
.bike-speedo span { font-size: 12px; color: #62695e; }
.bike-speedo small { grid-column: 1 / -1; font-size: 11px; color: #62695e; font-variant-numeric: tabular-nums; }
.bike-speedo-enter-active, .bike-speedo-leave-active { transition: opacity .25s ease, transform .25s ease; }
.bike-speedo-enter, .bike-speedo-leave-to { opacity: 0; transform: translateY(-6px); }
@keyframes bike-ring-in { from { opacity: 0; transform: scale(.7); } }
@keyframes bike-ring-draw { from { stroke-dasharray: 0 100; } to { stroke-dasharray: 3.2 2; } }
@keyframes bike-ring-march { to { stroke-dashoffset: -5.2; } }
@keyframes bike-ring-pulse { 0% { opacity: .45; transform: scale(1); } 100% { opacity: 0; transform: scale(1.16); } }
@keyframes bike-ring-turn { to { transform: rotate(360deg); } }
@keyframes bike-tip-in { from { opacity: 0; transform: translateY(calc(-50% + 4px)); } }
@media (max-width: 600px) {
  .bike-stage-tools { top: 6px; right: 6px; left: 6px; justify-content: space-between; gap: 6px; }
  .bike-views button, .bike-ride { min-height: 30px; padding: 0 8px; font-size: 11px; }
  .bike-speedo { top: 44px; bottom: auto; left: 6px; padding: 4px 10px 4px 6px; }
  .bike-speedo svg { width: 56px; height: 32px; }
  .bike-speedo strong { font-size: 20px; }
  .bike-hotspot-tip { font-size: 12px; padding: 8px 10px; gap: 3px 12px; max-width: 240px; }
}
@media (prefers-reduced-motion: reduce) {
  .bike-hotspot-ring, .bike-hotspot-ring circle, .bike-ride.is-riding svg { animation: none; }
  .bike-hotspot-pulse { display: none; }
}
</style>

<style scoped>
.bike-preview-error { position:absolute; bottom:12px; left:20px; right:20px; display:flex; flex-wrap:wrap; align-items:center; justify-content:center; gap:8px; padding:10px 14px; border:1px solid #ddd9cb; border-radius:8px; background:#f8f3e6; color:#554723; font-size:12px; line-height:1.4; }
.bike-preview-error button { text-decoration:underline; font-weight:600; padding:4px; }
.bike-preview-error button:focus-visible { outline:2px solid #554723; }
</style>

<style scoped>
@media(max-width:600px) { .bike-preview-error { left:8px; right:8px; bottom:4px; gap:4px; padding:4px 8px; font-size:11px; } }
</style>
